// React
import { createElement } from "react"

// UI
import { cn } from "@/lib/utils"
import { Heading } from "@/components/Typography"
import { PageTemplate } from "@/layouts/PageTemplate"

// Components
import { PublicProjectsMapsOverview } from "@/components/maps/PublicProjectsMapsOverview"
import { TestimonialCarousel } from "./_components/TestimonialCarousel"
import { InfoSection } from "./_components/InfoSection"

// Translations
import { useTrans } from "@/i18n"
import { useLang } from "@/context/lang"

// Queries
import { useAllPlatformStatisticsQuery } from "@/api/graphql"

// Env variables
import { GRAPHQL_URL } from "@/lib/env"

// Web components
import "@/pages/statistics/platform/_components/TotalProductionCounter"

/**
 * StatisticsImpact
 * @returns
 */
export function StatisticsImpact() {
	// Translations
	const { formatNumber } = useLang()
	const t = useTrans(["dashboard", "investments"])

	// Query
	const { data, isLoading } = useAllPlatformStatisticsQuery()
	const hardCodedStatsData = {
		// TODO: fetch this via API
		total_solar_panels: 2051119,
		capacity_in_mwp: 975.642408,
	}

	// Temp feature flag
	const ENABLE_ALL_IMPACT = false

	return (
		<PageTemplate>
			<div className="my-8 flex justify-center gap-4">
				<div>
					{isLoading ? (
						<>
							<Heading as="h5" className="mb-2">
								{t(
									"dashboard:dashboard.stats.platform_stats.kwh.title",
								)}
							</Heading>
							<div className="animate-pulse">
								<div className="h-12 w-64 rounded bg-gray-200"></div>
							</div>
						</>
					) : (
						<>
							<Heading as="h5" className="mb-2">
								{t(
									"dashboard:dashboard.stats.platform_stats.kwh.title",
								)}
							</Heading>
							{/** Web component for total-production-counter */}
							{createElement("total-production-counter", {
								"api-endpoint": GRAPHQL_URL,
							})}
						</>
					)}
				</div>
			</div>

			<TestimonialCarousel
				title={t("dashboard:dashboard.stats.impact.investors_title")}
				stats={
					isLoading
						? []
						: [
								{
									title: t(
										"dashboard:dashboard.stats.platform_stats.blocks.total_investors",
									),
									value: formatNumber(
										data?.platform_statistics
											?.total_investors ?? 0,
									),
								},
								/*{
										title: t(
											"dashboard:dashboard.stats.platform_stats.blocks.percentage_of_project_wise_repeated_investors",
										),
										value: `${formatNumber(data?.platform_statistics?.percentage_of_project_wise_repeated_investors ?? 0)}%`,
									},*/
							]
				}
				loading={isLoading}
			/>

			{/* Details about our durable energy */}
			{ENABLE_ALL_IMPACT && (
				<div className="mt-8 space-y-6">
					<InfoSection
						variant="right"
						title={t(
							"dashboard:dashboard.stats.impact.solar_panels_title",
						)}
						image="/assets/impact/solar-panels.png"
						stats={
							isLoading
								? []
								: [
										{
											title: t(
												"dashboard:dashboard.stats.platform_stats.blocks.total_solar_panels",
											),
											value: `${formatNumber(hardCodedStatsData.total_solar_panels)} +`,
										},
										{
											title: t(
												"dashboard:dashboard.stats.platform_stats.blocks.capacity_in_mwp",
											),
											value: `${formatNumber(hardCodedStatsData.capacity_in_mwp)} + Mwp`,
										},
									]
						}
						loading={isLoading}
					/>

					<InfoSection
						variant="left"
						title={t(
							"dashboard:dashboard.stats.impact.batteries_title",
						)}
						image="/assets/impact/battery-storage.png"
						stats={
							isLoading
								? []
								: [
										{
											title: t(
												"dashboard:dashboard.stats.impact.battery_count",
											),
											value: "100+",
										},
										{
											title: t(
												"dashboard:dashboard.stats.impact.storage_capacity",
											),
											value: "100kwh+",
										},
									]
						}
						loading={isLoading}
					/>

					<InfoSection
						variant="right"
						title={t(
							"dashboard:dashboard.stats.impact.wind_turbines_title",
						)}
						image="/assets/impact/windmill.jpeg"
						stats={
							isLoading
								? []
								: [
										{
											title: t(
												"dashboard:dashboard.stats.impact.wind_turbine_count",
											),
											value: "1",
										},
										{
											title: "Geproduceerd in 2024!",
											value: "1.724.468kwh",
										},
									]
						}
						loading={isLoading}
					/>
				</div>
			)}

			{/* Header and image section */}
			<div className={"mx-auto mt-4 w-4/5"}>
				<Heading
					as="h3"
					className="mb-4 text-center text-2xl font-semibold"
				>
					{t(
						"dashboard:dashboard.stats.impact.projects_throughout_europe",
					)}
				</Heading>
				<div
					className="relative w-full"
					style={{ paddingBottom: "75%" }}
				>
					<div
						className={cn(
							"absolute inset-0 rounded-lg bg-gray-200",
							isLoading && "animate-pulse",
						)}
					>
						{!isLoading && <PublicProjectsMapsOverview />}
					</div>
				</div>
			</div>
		</PageTemplate>
	)
}
